/**
 * Card Component - CSS-First Design Token Architecture
 *
 * Token-based card component that adapts to the current LayoutTheme.
 * Uses CSS classes for states and layout-specific styling.
 *
 * Padding: sm, md, lg
 * Layout Variants: modern-cards, matrix-terminal, mac-os-classic, executive-dashboard
 */

import { HTMLAttributes, ReactNode } from 'react'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  padding?: 'sm' | 'md' | 'lg'
  /** Enable hover effects for clickable cards */
  interactive?: boolean
  /** Explicitly set variant, overrides layout theme */
  variant?: 'default' | 'elevated' | 'outlined' | 'flat'
}

export function Card({
  children,
  padding = 'md',
  interactive = false,
  variant,
  className = '',
  ...props
}: CardProps) {
  const { themeConfig } = useLayoutTheme()

  // Determine variant from props or layout theme
  const effectiveVariant = variant ?? getLayoutVariant(themeConfig.id)

  // Build class string for CSS-first styling
  const classes = [
    'card',
    `card--${effectiveVariant}`,
    `card--padding-${padding}`,
    interactive ? 'card--interactive' : '',
    `card--layout-${themeConfig.id}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article
      className={classes}
      {...props}
    >
      {children}
    </article>
  )
}

/** Map layout theme to card variant */
function getLayoutVariant(layoutId: string): string {
  switch (layoutId) {
    case 'executive-dashboard':
      return 'flat'
    case 'matrix-terminal':
      return 'outlined'
    case 'mac-os-classic':
      return 'elevated'
    default:
      return 'default'
  }
}
